import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  decodePuzzleId,
  evaluateExpression,
  interleaveWithOps,
  generateRandomPuzzle,
  getSolutionDigitsFromSolution,
  unobfuscate,
  obfuscate,
} from "./utils";
import PuzzleHeader from "./components/PuzzleHeader";
import DigitsBar from "./components/DigitsBar";
import ExpressionLine from "./components/ExpressionLine";
import HelpModal from "./components/HelpModal";
import SuccessDialog from "./components/SuccessDialog";
import ErrorModal from "./components/ErrorModal";
import { Puzzle } from "./types";
import "./styles.css";
import { findIndex } from "lodash";

const DEFAULT_NUMBERS_TO_GUESS = 5;
export const HOW_MANY_GUESSES = 6;
export const MAX_GOAL = 100;
export const MIN_GOAL = 0;
const logEvent=(eventName:string, params?:object)=>{
  //@ts-ignore
  if(window.gtag)
  {
    //@ts-ignore
    window.gtag('event', eventName, params);
  }
}
const getInitialGuess = (guesses: number) => Array(guesses).fill([]);

const App: React.FC = () => {
  const [numbersToGuess, setNumbersToGuess] = useState<number>(
    DEFAULT_NUMBERS_TO_GUESS
  );
  const [puzzle, setPuzzle] = useState<Puzzle>(decodePuzzleId("empty"));

  useEffect(() => {
    const handleLocationChange = () => {
      const hash = window.location.hash.substring(1); // Remove the '#' character
      let puzzleParam =  unobfuscate(hash);

      if (!puzzleParam || puzzleParam === "empty") {
        const puzzle = generateRandomPuzzle(numbersToGuess);
        puzzleParam = puzzle.puzzleId;
        window.location.hash =obfuscate( puzzleParam); // Set the hash
      }
      const decodedPuzzle = decodePuzzleId(puzzleParam);
      setPuzzle(decodedPuzzle);
    };
    window.addEventListener("hashchange", handleLocationChange);
    handleLocationChange(); // Call it initially to set the puzzle
    return () => {
      window.removeEventListener("hashchange", handleLocationChange);
    };
  }, [numbersToGuess]);
  const [activeLineIndex, setActiveLineIndex] = useState<number>(0);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);

  const [usedButtons, setUsedButtons] = useState<string[][]>([
    ...getInitialGuess(HOW_MANY_GUESSES),
  ]);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [proximateSolution, setProximateSolution] = useState<string | null>(
    null
  );
  const [dialogStatus, setDialogStatus] = useState<
    "success" | "failure" | undefined
  >(undefined);
  const [buttonStatus, setButtonStatus] = useState<string[]>(
    Array(9).fill("light-gray")
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  const updateButtonStatus = useCallback(
    (proximateSolution: string | null, usedButtons: string[][]) => {
      if (!proximateSolution) {
        return;
      }
      setButtonStatus((prevStatus) => {
        const newStatus = [...prevStatus];
        const solutionDigits = getSolutionDigitsFromSolution(proximateSolution);
        usedButtons.forEach((usedButtonsLine, index) => {
          if (usedButtonsLine.length === numbersToGuess) {
            usedButtonsLine.forEach((button, indexOfButtonInLine) => {
              if (solutionDigits.includes(button) === false) {
                newStatus[parseInt(button) - 1] = "dark-gray";
              }
              if (solutionDigits[indexOfButtonInLine] === button) {
                newStatus[parseInt(button) - 1] = "green";
              }
              if (
                solutionDigits.includes(button) &&
                newStatus[parseInt(button) - 1] !== "green"
              ) {
                newStatus[parseInt(button) - 1] = "yellow";
              }
            });
          }
        });
        return newStatus;
      });
    },
    [numbersToGuess]
  );

  useEffect(() => {    
    logEvent("game_start");
    setActiveLineIndex(0);
    setUsedButtons([...getInitialGuess(HOW_MANY_GUESSES)]);
    setShowSuccess(false);
    setDialogStatus(undefined);
    setButtonStatus(Array(9).fill("light-gray"));
    setProximateSolution(puzzle.solutions[0]);
    setErrorMessage("");
  }, [puzzle]);
  const handleNewPuzzle = useCallback((): void => {
    const newPuzzle = generateRandomPuzzle(numbersToGuess);
    window.location.hash = obfuscate(newPuzzle.puzzleId); // Update URL hash
    setActiveLineIndex(0);
    setUsedButtons([...getInitialGuess(HOW_MANY_GUESSES)]);
    setShowSuccess(false);
    setDialogStatus(undefined);
    setButtonStatus(Array(9).fill("light-gray"));
    setProximateSolution(newPuzzle.solutions[0]);
    setErrorMessage("");
  }, [numbersToGuess]);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.hash =obfuscate(puzzle.puzzleId);
    window.history.replaceState({}, "", url);
  }, [puzzle]);

  const expressions = useMemo(() => {
    return usedButtons.map((buttons) => {
      let numbers: string[] = buttons.map((button) => button);
      while (numbers.length < numbersToGuess) {
        numbers.push(" ");
      }
      let newExpression = interleaveWithOps(numbers, puzzle.operations);
      return newExpression;
    });
  }, [numbersToGuess, puzzle.operations, usedButtons]);

  const calculatedValues = useMemo(() => {
    return expressions.map((expression) => {
      if (expression.includes(" ")) {
        return NaN;
      }
      let endOfExpression = findIndex(expression, (i) => i === " ") - 1;
      if (endOfExpression === -2) {
        endOfExpression = expression.length;
      }
      const newExp = expression.substring(0, endOfExpression);
      const value = evaluateExpression(newExp);
      return value;
    });
  }, [expressions]);

  const handleDigitClick = useCallback(
    (digit: string, buttonIndex: number): void => {
      if (digit === "delete") {
        setUsedButtons((prev) => {
          const newUsedButtons = [...prev];
          if (newUsedButtons[activeLineIndex].length > 0) {
            newUsedButtons[activeLineIndex].pop();
          }
          return newUsedButtons;
        });
        return;
      }
if( usedButtons[activeLineIndex].length< numbersToGuess){
      setUsedButtons((prev) => {
        const newUsedButtons = [...prev];
        newUsedButtons[activeLineIndex] = [
          ...newUsedButtons[activeLineIndex],
          digit,
        ];
        return newUsedButtons;
      });}
    },
    [activeLineIndex, numbersToGuess, usedButtons]
  );

  const bottomLimit = useMemo(() => {
    const belowTarget = calculatedValues.slice(0,activeLineIndex).filter(
      (guess) => guess <= puzzle.targetNumber
    );
    belowTarget.push(MIN_GOAL);
    return Math.floor(Math.max(...belowTarget));
  }, [activeLineIndex, calculatedValues, puzzle.targetNumber]);

  const topLimit = useMemo(() => {
    const aboveTarget = calculatedValues.slice(0,activeLineIndex).filter(
      (guess) => guess >= puzzle.targetNumber
    );
    aboveTarget.push(MAX_GOAL);
    return Math.ceil(Math.min(...aboveTarget));
  }, [activeLineIndex, calculatedValues, puzzle.targetNumber]);

  const goalLabel = useMemo(() => {
    if(bottomLimit===topLimit){
      return `Goal = ${topLimit}`;
    }
    return `${bottomLimit} < Goal < ${topLimit}`;
  }, [topLimit, bottomLimit]);

  const handleSubmit = useCallback(() => {
    const isFullLine = usedButtons[activeLineIndex].length === numbersToGuess;
    if (isFullLine) {
      const userDigits = usedButtons[activeLineIndex];
      let bestMatch = {
        index: -1,
        matchCount: 0,
        earliestMatch: numbersToGuess,
      };
      let newProximateSolution = proximateSolution;
      if (!newProximateSolution) {
        puzzle.solutions.forEach((solution, solIndex) => {
          const solutionDigits = solution.replace(/[^0-9]/g, "").split("");
          let matchCount = 0;
          let earliestMatch = numbersToGuess;

          solutionDigits.forEach((digit, idx) => {
            if (digit === userDigits[idx]) {
              matchCount++;
              if (idx < earliestMatch) {
                earliestMatch = idx;
              }
            }
          });

          if (
            matchCount > bestMatch.matchCount ||
            (matchCount === bestMatch.matchCount &&
              earliestMatch < bestMatch.earliestMatch)
          ) {
            bestMatch = { index: solIndex, matchCount, earliestMatch };
          }
        });

        newProximateSolution = puzzle.solutions[bestMatch.index];
        console.log("found Proximate Solution:", newProximateSolution);
        setProximateSolution(newProximateSolution);
      }


      // 1. Check if line has the right number of digits
      const isFullLine = usedButtons[activeLineIndex].length === numbersToGuess;
      if (!isFullLine) {
        setErrorMessage("Please fill all slots before submitting");
        return;
      }

      // 2. Check if it calculates to an integer
      const currentValue = calculatedValues[activeLineIndex];
      if (!Number.isInteger(currentValue)) {
        setErrorMessage("Expression must calculate to an integer number");
        return;
      }

      if ((!(currentValue===topLimit && currentValue===bottomLimit)) && (currentValue <= bottomLimit || currentValue >= topLimit)) {
        setErrorMessage(
          `Expression must be between ${bottomLimit} and ${topLimit}`
        );
        return;
      }

      // Validation passed, proceed with submission
      if (!proximateSolution) {
        return;
      }
      updateButtonStatus(newProximateSolution, usedButtons);
      const isCorrectOrder =
        getSolutionDigitsFromSolution(proximateSolution)?.join() ===
        userDigits.join();

      if (isCorrectOrder) {
        logEvent("puzzle_solved");
        logEvent("puzzle_solved"+activeLineIndex)
        setShowSuccess(true);
        setActiveLineIndex(activeLineIndex + 1);
        
        setDialogStatus("success");
      } else if (activeLineIndex === HOW_MANY_GUESSES - 1) {
        logEvent("puzzle_fail");        
        
        setShowSuccess(true);
        setDialogStatus("failure");
      } else {
        setActiveLineIndex(activeLineIndex + 1);
      }
    }
  }, [usedButtons, activeLineIndex, numbersToGuess, proximateSolution, updateButtonStatus, calculatedValues, bottomLimit, topLimit, puzzle.solutions]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Handle numbers
      if (/^[0-9]$/.test(event.key)) {
        // Find the first occurrence of this number in puzzle.digits
        const buttonIndex = puzzle.digits.findIndex((d) => d === event.key);
        if (buttonIndex !== -1) {
          handleDigitClick(event.key, buttonIndex);
        }
      }
      // Handle Enter key for submission
      if (event.key === "Enter") {
        handleSubmit();
      }
    },
    [puzzle.digits, handleDigitClick, handleSubmit]
  );

  useEffect(() => {
    // Add keyboard event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  console.log("proximateSolution", proximateSolution, activeLineIndex);
  return (
    <div className="app-container">
      <PuzzleHeader
        puzzleId={puzzle.puzzleId}
        onOpenHelp={() => setHelpOpen(true)}
        numbersCount={numbersToGuess}
        onNumbersCountChange={(count) => {
          setNumbersToGuess(count);
          // Generate a new puzzle with the new number count
          const newPuzzle = generateRandomPuzzle(count);
          window.location.hash = newPuzzle.puzzleId;
        }}
    />
      <div className="target-container">
        <div className="target-number">{goalLabel}</div>
      </div>
      <div className="expressions-container">
        {expressions.map((exp, index) => (
          <ExpressionLine
            key={index}
            lineNumber={index}
            currentActiveLineIndex={activeLineIndex}
            usedNumbers={usedButtons[index]}
            expression={exp}
            calculatedValue={calculatedValues[index]}
            targetNumber={puzzle.targetNumber}
            // disabled={index !== activeLineIndex}
            proximateSolution={
              index < activeLineIndex ? proximateSolution : null
            }
          />
        ))}
      </div>
      <div className="digits-bar-container">
        <DigitsBar
          digits={puzzle.digits}
          onDigitClick={handleDigitClick}
          usedButtons={usedButtons[activeLineIndex]}
          approximateSolution={proximateSolution}
          activeLineIndex={activeLineIndex}
          buttonStatus={buttonStatus}
        />
      </div>
      <div className="submit-container">
        <button className="submit-button" onClick={handleSubmit}>
          Submit
        </button>
      </div>
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <SuccessDialog
        isOpen={showSuccess}
        onClose={handleNewPuzzle}
        status={dialogStatus}
        theCorrectSolution={"the solution was "+puzzle.solutions[0]+" = "+puzzle.targetNumber}
      />
      <ErrorModal
        isOpen={!!errorMessage}
        message={errorMessage}
        onClose={() => setErrorMessage("")}
      />
    </div>
  );
};

export default App;
